import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import styled from 'styled-components';
import ErrorBoundary from '../../../shared/error-boundary';
import Loading from '../../../shared/loading';
import showModal from './show-modal';
import { useForm } from '../../../shared/form-validator-hook';
import useRegister from '../../shared/sign-up/hooks';
import oauthProviderDisplayName from '../../shared/sign-up/oauth-provider-label';
import FirstName from '../../shared/sign-up/first-name';
import LastName from '../../shared/sign-up/last-name';
import Email from '../../shared/sign-up/email';
import Password from '../../shared/sign-up/password';
import ConfirmPassword from '../../shared/sign-up/confirm-password';
import TCP from '../../shared/sign-up/tcp';
import { SmallButton } from './styles';

const Input = styled.input`
  border-radius: 3px;
  border: 1px solid ${props => props.theme.secondaryColorLight};
  height: 40px;
  line-height: 40px;
  padding-left: 10px;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightMedium};
  font-size: 11px;
  color: ${props => props.theme.bodyColor};


  &::placeholder {
    color: ${props => props.theme.secondaryColor};
    text-transform: capitalize;
  }
`;

/* stylelint-disable selector-type-no-unknown */
const HelpBlock = styled.label`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightHeavy};
  font-size: 11px;
  color: ${props => props.theme.errorColor};

  ${Input}:not([data-error="show"]) + & {
    display: none;
  }
`;
/* stylelint-enable selector-type-no-unknown */

const Checkbox = styled.input`
  width: 10px;
  margin-right: 8px;
`;

const Label = styled.label`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightMedium};
  font-size: 11px;
  line-height: 14px;
`;

const Button = styled(SmallButton)`
  width: 100%;
`;

const H4 = styled.h4`
  display: flex;
  flex-direction: column;

  > span {
    color: ${props => props.theme.bodyColor};
    font-family: ${props => props.theme.fontFamilyBody};
    font-weight: ${props => props.theme.fontWeightHeavy};
    font-size: 25px;
    line-height: 25px;
    text-align: left;
  }
`;

const ReflowContainer = styled.section`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
`;

const InputContainer = styled.div`
  height: 40px;
  margin: 0 0 20px 0;
  flex-grow: 0;
  flex-shrink: ${props => (props.size === 'half' ? 0.5 : 1)};
  flex-basis: ${props => (props.size === 'half' ? '188px' : '410px')};

  &:nth-child(1) {
    margin-right: 5px;
  }

  &:nth-child(2) {
    margin-left: 5px;
  }

  &:last-child {
    display: flex;
  }

  > ${Input} {
    width: 100%;
  }
`;

const Hint = styled.p`
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: ${props => props.theme.fontWeightMedium};
  font-size: 11px;
  line-height: 14px;
`;

const ErrorMessage = styled(HelpBlock)`
  &&&& {
    display: inline;
  }
`;

export default function SignUpModal({ visible, email }) {
  const loadingRef = React.useRef();
  const [prefill, setPrefill] = React.useState(null);
  const [focus, setFocus] = React.useState(false);
  const {
    values, useInput, isValid, errors,
  } = useForm({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordVerification: '',
    tc: false,
  });
  const {
    isEmailUnique, isLoading, oauthProvider, start,
  } = useRegister({
    doSubscription: false,
    data: values,
    onEmailNotUnique: () => _.get(loadingRef, 'current') && loadingRef.current.stop(),
    onComplete: () => {
      _.get(loadingRef, 'current') && loadingRef.current.stop();
      window.location.reload();
    },
  });

  React.useEffect(() => {
    if (isLoading) {
      _.get(loadingRef, 'current') && loadingRef.current.start();
    }
  }, [isLoading]);

  const submit = e => {
    e.preventDefault();
    start();
  };

  const onShow = () => {
    if (_.get(email, 'length')) {
      setPrefill(email);
    }
    setFocus(true);
  };

  const commonProps = {
    Field: Input,
    Err: HelpBlock,
    useInput,
    errors,
  };

  return (
    <ErrorBoundary>
      <Modal
        centered="true"
        dialogAs="div"
        dialogClassName="sign-up-modal"
        backdrop="static"
        show={visible}
        onShow={onShow}
      >
        <article>
          <header>
            <H4>
              <span>register today</span>
              <span>to access livewire</span>
            </H4>
          </header>
          <ReflowContainer>
            <InputContainer size="half">
              <FirstName {...commonProps} focus={focus} />
            </InputContainer>
            <InputContainer size="half">
              <LastName {...commonProps} />
            </InputContainer>
            <InputContainer>
              <Email {...commonProps} prefill={prefill} />
              {(!errors.email && !isEmailUnique) && (
                <ErrorMessage>
                  {oauthProvider ? (
                    <>
                      Email is active using
                      {' '}
                      {oauthProviderDisplayName(oauthProvider)}
                      {' '}
                      oAuth.
                      {' '}
                      <a href="/sign_in">Sign In</a>
                      {' '}
                      to access.
                    </>
                  ) : (
                    <>
                      Email already in use.
                      {' '}
                      <a href="https://www.livewiremarkets.com/sign_in">Sign In</a>
                      {' '}
                      to access your account.
                    </>
                  )}
                </ErrorMessage>
              )}
            </InputContainer>
            <InputContainer>
              <Password {...commonProps} />
            </InputContainer>
            <InputContainer>
              <ConfirmPassword {...commonProps} password={values.password} />
            </InputContainer>
            <InputContainer>
              <TCP
                {...{
                  Checkbox, Label, useInput, checked: !!values.tc,
                }}
              />
            </InputContainer>
          </ReflowContainer>
          <section>
            <Loading variant="button" ref={loadingRef}>
              <Button
                onClick={submit}
                disabled={!(isValid && values.tc && isEmailUnique)}
              >
                join free
              </Button>
            </Loading>
          </section>
          <section>
            <Hint>
              {'Already a member? '}
              <a href="#" onClick={showModal('sign-in')}>Sign in here.</a>
            </Hint>
          </section>
        </article>
      </Modal>
    </ErrorBoundary>
  );
}

SignUpModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  email: PropTypes.string,
};

SignUpModal.defaultProps = {
  email: null,
};
